import { useEffect, useRef, useState } from "react";
import { Loader2, ScanLine, X, RotateCcw, Sparkles } from "lucide-react";
import { fotoApi, fotoError } from "./fotoApi";
import { grabFrame, readPlateOnDevice } from "./plateOcr";

const BTN = "h-12 rounded-xl px-4 text-base font-semibold flex items-center justify-center gap-2";

const Result = ({ st, onSelect, onRetry, onAi, aiEnabled }) => (
  <div className="space-y-3" data-testid="targa-result">
    {st.targa && <div className="text-sm text-slate-600">Targa letta: <b className="font-mono text-lg text-slate-900 tracking-wider" data-testid="targa-letta">{st.targa}</b></div>}
    {st.list.length ? (
      <ul className="space-y-2" data-testid="targa-results">
        {st.list.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => onSelect(p)} data-testid={`targa-result-${p.id}`}
              className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 active:bg-amber-50 transition-colors">
              <div className="text-base font-semibold text-slate-900">{p.cliente || "Cliente non indicato"}</div>
              <div className="text-sm text-slate-500 font-mono">{p.numero_pratica}{p.targa ? ` · ${p.targa}` : ""}</div>
            </button>
          </li>
        ))}
      </ul>
    ) : (
      <p className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="targa-empty">
        {st.targa ? <>Nessuna pratica aperta trovata per la targa <b className="font-mono">{st.targa}</b>.</> : "Targa non leggibile. Avvicinati e inquadra bene la targa."}
      </p>
    )}
    <div className="flex gap-2">
      <button type="button" onClick={onRetry} data-testid="targa-retry" className={`${BTN} flex-1 bg-amber-500 text-slate-900`}><RotateCcw className="h-5 w-5" /> Riprova</button>
      {aiEnabled && st.source === "device" && !st.list.length && (
        <button type="button" onClick={onAi} data-testid="targa-ai" className={`${BTN} flex-1 border border-slate-300 bg-white text-slate-800`}><Sparkles className="h-5 w-5 text-amber-500" /> Rileggi con AI</button>
      )}
    </div>
  </div>
);

export const FotoTarga = ({ mode = "device", aiEnabled = false, onSelect, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const [phase, setPhase] = useState("camera");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [st, setSt] = useState(null);

  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  const start = async () => {
    setErr(""); setSt(null); setPhase("camera");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 } }, audio: false });
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play().catch(() => {}); }
    } catch { setErr("Impossibile accedere alla fotocamera. Consenti l'accesso dalle impostazioni del browser."); }
  };
  useEffect(() => { start(); return stop; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cerca = async (targa, source) => {
    if (!targa) { setSt({ targa: "", list: [], source }); setPhase("result"); return; }
    setMsg("Cerco le pratiche aperte…");
    const { data } = await fotoApi.get("/pratiche-targa", { params: { targa } });
    setSt({ targa, list: data || [], source }); setPhase("result");
  };

  const leggiAi = async () => {
    setPhase("busy"); setMsg("Lettura con intelligenza artificiale…"); setErr("");
    try {
      const img = frameRef.current.toDataURL("image/jpeg", 0.85);
      const { data } = await fotoApi.post("/ocr-targa", { image: img });
      await cerca(data.targa, "ai");
    } catch (e) { setErr(fotoError(e)); setPhase("result"); setSt((s) => s || { targa: "", list: [], source: "ai" }); }
  };

  const leggi = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    frameRef.current = grabFrame(v, false);
    const crop = grabFrame(v, true);
    stop();
    if (mode === "ai") { await leggiAi(); return; }
    setPhase("busy"); setMsg("Lettura targa…");
    let targa = "";
    try { targa = await readPlateOnDevice(crop); } catch { targa = ""; }
    try { await cerca(targa, "device"); } catch (e) { setErr(fotoError(e)); setPhase("result"); }
  };

  const close = () => { stop(); onClose(); };

  return (
    <div className="space-y-3" data-testid="foto-targa">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">{mode === "ai" ? <Sparkles className="h-5 w-5 text-amber-500" /> : <ScanLine className="h-5 w-5 text-amber-500" />} {mode === "ai" ? "Cerca targa con AI" : "Cerca targa"}</h2>
        <button type="button" onClick={close} data-testid="targa-close" className="h-10 w-10 grid place-items-center rounded-full hover:bg-slate-200" aria-label="Chiudi"><X className="h-5 w-5" /></button>
      </div>
      {err && <p className="text-sm text-red-700" role="alert" data-testid="targa-error">{err}</p>}
      {phase === "camera" && (
        <>
          <div className="relative overflow-hidden rounded-xl bg-black aspect-[3/4] sm:aspect-video">
            <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-cover" data-testid="targa-video" />
            <div className="absolute left-[8%] right-[8%] top-[34%] h-[32%] rounded-lg border-4 border-amber-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
            <p className="absolute bottom-3 inset-x-0 text-center text-sm text-white/90">Inquadra la targa dentro il riquadro</p>
          </div>
          <button type="button" onClick={leggi} data-testid="targa-leggi" className={`${BTN} w-full bg-amber-500 text-slate-900 h-14 text-lg`}><ScanLine className="h-6 w-6" /> Leggi targa</button>
        </>
      )}
      {phase === "busy" && <div className="py-16 flex flex-col items-center gap-3 text-slate-600" data-testid="targa-busy"><Loader2 className="h-8 w-8 animate-spin text-amber-500" />{msg}</div>}
      {phase === "result" && st && <Result st={st} onSelect={(p) => { stop(); onSelect(p); }} onRetry={start} onAi={leggiAi} aiEnabled={aiEnabled} />}
    </div>
  );
};
